import React, { useState, useEffect, useRef } from 'react';
import config from '../../config/apiConfig';

const ENDPOINT_URL = (config.api_url || 'http://localhost:5000').replace(/\/$/, '') + '/api/chat';

const getOrCreateSessionId = () => {
    let storedId = localStorage.getItem('ventagixChatSessionId');
    if (!storedId) {
        storedId = 'session_' + Date.now() + Math.floor(Math.random() * 1000); 
        localStorage.setItem('ventagixChatSessionId', storedId);
    }
    return storedId;
};

// ==============================================================================
// 🔧 FUNCIÓN PARA RENDERIZAR TEXTO CON **NEGRITA** COMO JSX
// ==============================================================================
const renderTextoConNegritas = (texto) => {
    if (!texto) return null;
    // Partir el texto por **...**
    const partes = texto.split(/(\*\*.*?\*\*)/g);
    return partes.map((parte, i) => {
        if (parte.startsWith('**') && parte.endsWith('**')) {
            return <strong key={i}>{parte.slice(2, -2)}</strong>;
        }
        return <span key={i}>{parte}</span>;
    });
};

// ==============================================================================
// 🎨 COMPONENTE PARA RENDERIZAR RESPUESTA ESTRUCTURADA
// ==============================================================================
const RespuestaEstructurada = ({ data }) => {
    if (!data) return null;

    const irA = (url) => {
        window.location.href = url;
    };

    return (
        <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
            {/* TÍTULO */}
            <div style={{ 
                fontWeight: 700, fontSize: '15px', marginBottom: '10px',
                display: 'flex', alignItems: 'center', gap: '6px'
            }}>
                <span>{data.emoji}</span>
                <span>{data.titulo}</span>
            </div>

            {/* PASOS */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {data.pasos && data.pasos.map((paso, idx) => (
                    <div key={idx} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '8px'
                    }}>
                        <span style={{
                            backgroundColor: '#f26b21', color: 'white',
                            borderRadius: '50%', width: '20px', height: '20px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '11px', fontWeight: 700, flexShrink: 0, marginTop: '2px'
                        }}>
                            {idx + 1}
                        </span>
                        <span style={{ flex: 1 }}>
                            {renderTextoConNegritas(paso)}
                        </span>
                    </div>
                ))}
            </div>

            {/* TIP */}
            {data.tip && (
                <div style={{
                    marginTop: '10px', padding: '8px 10px',
                    backgroundColor: '#fff8e1', borderRadius: '8px',
                    border: '1px solid #ffe082', fontSize: '12px',
                    display: 'flex', alignItems: 'flex-start', gap: '6px'
                }}>
                    <span>💡</span>
                    <span>{renderTextoConNegritas(data.tip)}</span>
                </div>
            )}

            {/* LINK */}
            {data.link && data.link.url && (
                <button
                    onClick={() => irA(data.link.url)}
                    style={{
                        marginTop: '10px', width: '100%', padding: '8px 14px',
                        backgroundColor: '#f26b21', color: 'white', border: 'none',
                        borderRadius: '8px', fontWeight: 600, fontSize: '13px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: '6px'
                    }}
                >
                    <span>🔗</span>
                    <span>{data.link.texto || 'Ir ahora'}</span>
                </button>
            )}
        </div>
    );
};

// ==============================================================================
// 🧠 COMPONENTE PRINCIPAL DEL CHATBOT
// ==============================================================================
const Chatbot = () => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState(''); 
    const [sessionId, setSessionId] = useState(null); 
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        const id = getOrCreateSessionId();
        setSessionId(id);
        postMessageToBackend("Iniciar conversación", id, true);
    }, []); 

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const postMessageToBackend = async (messageText, currentSessionId, isInitialMessage = false) => {
        if (!messageText.trim() && !isInitialMessage) return;
        if (isLoading && !isInitialMessage) return;

        setIsLoading(true);

        try {
            if (!isInitialMessage) {
                const newUserMessage = { sender: 'user', text: messageText };
                setMessages((prevMessages) => [...prevMessages, newUserMessage]);
            }
            
            const response = await fetch(ENDPOINT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    message: messageText,
                    session_id: currentSessionId
                }),
            });

            if (!response.ok) {
                throw new Error(`Error HTTP! estado: ${response.status}`);
            }

            const data = await response.json();
            
            const botMessage = {
                sender: 'bot',
                text: data.response,
                estructurado: data.estructurado || null
            };
            
            setMessages((prevMessages) => [...prevMessages, botMessage]);

        } catch (error) {
            console.error("Error al enviar el mensaje:", error);
            setMessages((prevMessages) => [
                ...prevMessages,
                { sender: 'bot', text: 'Lo siento, hubo un error al conectar con el servidor. Inténtalo de nuevo.' }
            ]);
        } finally {
            setIsLoading(false);
            setInputMessage('');
        }
    };
    
    const handleSendInput = (e) => {
        e.preventDefault();
        if (!inputMessage.trim()) return;
        postMessageToBackend(inputMessage, sessionId);
    };

    return (
        <div className="d-flex flex-column h-100">
            {/* Área de mensajes */}
            <div className="flex-grow-1 overflow-auto p-3" style={{ backgroundColor: '#f8f9fa' }}>
                {messages.map((msg, index) => (
                    <div
                        key={index}
                        className={`d-flex ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'} mb-3`}
                    >
                        {/* Avatar BOT (izquierda) */}
                        {msg.sender === 'bot' && (
                            <div style={{
                                width: '30px', height: '30px', borderRadius: '50%',
                                backgroundColor: '#f26b21', color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '15px', flexShrink: 0, marginRight: '8px'
                            }}>
                                🤖
                            </div>
                        )}

                        {/* Burbuja de mensaje */}
                        {msg.estructurado ? (
                            <div style={{
                                backgroundColor: 'white', borderRadius: '12px',
                                padding: '14px', maxWidth: '85%',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                border: '1px solid #e8e8e8'
                            }}>
                                <RespuestaEstructurada data={msg.estructurado} />
                            </div>
                        ) : (
                            <div style={{
                                padding: '10px 14px', borderRadius: '12px',
                                maxWidth: '80%', fontSize: '13px', lineHeight: '1.4',
                                backgroundColor: msg.sender === 'user' ? '#f26b21' : 'white',
                                color: msg.sender === 'user' ? 'white' : '#333',
                                boxShadow: msg.sender === 'bot' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                border: msg.sender === 'bot' ? '1px solid #e8e8e8' : 'none'
                            }}>
                                {msg.text}
                            </div>
                        )}

                        {/* Avatar USER (derecha) */}
                        {msg.sender === 'user' && (
                            <div style={{
                                width: '30px', height: '30px', borderRadius: '50%',
                                backgroundColor: '#198754', color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '14px', fontWeight: 'bold', flexShrink: 0, marginLeft: '8px'
                            }}>
                                U
                            </div>
                        )}
                    </div>
                ))}

                {isLoading && (
                    <div className="d-flex justify-content-start mb-2">
                        <div className="spinner-border text-primary" role="status" style={{width: '20px', height: '20px'}}>
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Área de Input */}
            <form onSubmit={handleSendInput} className="p-2 border-top" style={{ backgroundColor: 'white' }}>
                <div className="input-group input-group-sm">
                    <input
                        type="text"
                        className="form-control"
                        placeholder={isLoading ? "Esperando respuesta..." : "Escribe tu mensaje..."}
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        disabled={isLoading}
                        style={{ borderRadius: '8px 0 0 8px', fontSize: '13px' }}
                    />
                    <button
                        type="submit"
                        className="btn"
                        disabled={isLoading}
                        style={{ 
                            backgroundColor: '#f26b21', color: 'white', 
                            borderRadius: '0 8px 8px 0', fontWeight: 600,
                            border: 'none', padding: '4px 14px'
                        }}
                    >
                        {isLoading ? '...' : 'Enviar'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Chatbot;
